import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import {
  publications,
  publicationBodies,
  publicationSlugs,
} from "@/brand/content";
import { SiteHeader5 } from "@/components/layout/SiteHeader5";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InteriorHero } from "@/components/common/InteriorHero";
import { ConsultationBanner } from "@/components/common/ConsultationBanner";

/**
 * PublicationPage — dynamic template for individual publications.
 *
 * Route: /concept-e/insights/:slug
 */
export default function PublicationPage() {
  const { slug } = useParams();
  const idx = publicationSlugs[slug];
  if (idx === undefined) return <Navigate to="/concept-e/insights" replace />;

  const pub = publications[idx];
  const body = publicationBodies[idx];

  // Related publications (next two in the list, wrapping)
  const related = [1, 2].map(
    (offset) => publications[(idx + offset) % publications.length]
  );

  return (
    <main
      data-testid="page-publication"
      className="min-h-screen"
      style={{
        background: C.surfaceLight2,
        color: C.textOnLight,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <SiteHeader5 />

      <InteriorHero
        breadcrumb={[
          { label: "Faith 1st", href: "/concept-e" },
          { label: "Insights", href: "/concept-e/insights" },
          { label: pub.tag },
        ]}
        eyebrow={`${pub.tag} · ${pub.date}`}
        title={pub.title}
        intro={pub.kicker}
        meta={[
          ["Format", pub.length],
          ["Category", pub.tag],
          ["Published", pub.date],
        ]}
      />

      {/* Article body */}
      <article
        data-testid="publication-body"
        style={{
          background: C.surfaceLight2,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-14">
            {/* Article rail */}
            <div className="lg:col-span-8 space-y-12">
              {body.sections.map((s, i) => (
                <section
                  key={i}
                  data-testid={`publication-section-${i}`}
                  className="space-y-5"
                >
                  <div className="flex items-baseline gap-4">
                    <span
                      className="font-cormorant tabular-nums"
                      style={{
                        color: C.accentMetal,
                        fontSize: "1.5rem",
                        lineHeight: 1,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2
                      className="font-cormorant"
                      style={{
                        color: C.textOnLight,
                        fontSize: "clamp(1.4rem, 2.2vw, 1.8rem)",
                        fontWeight: 400,
                        lineHeight: 1.15,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {s.heading}
                    </h2>
                  </div>
                  <p
                    className="font-inter leading-[1.8]"
                    style={{
                      color: C.inkOnLight,
                      fontSize: "clamp(1.02rem, 1.1vw, 1.1rem)",
                      maxWidth: "62ch",
                    }}
                  >
                    {s.body}
                  </p>
                </section>
              ))}
            </div>

            {/* Pull quote + sidebar */}
            <aside className="lg:col-span-4 lg:pt-4">
              <div className="sticky top-32 space-y-10">
                <blockquote
                  className="p-8"
                  data-testid="publication-pullquote"
                  style={{
                    background: C.surfaceLight,
                    borderTop: `1px solid ${C.accentMetal}`,
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  <p
                    className="font-cormorant italic"
                    style={{
                      color: C.textOnLight,
                      fontSize: "clamp(1.2rem, 1.4vw, 1.4rem)",
                      lineHeight: 1.35,
                    }}
                  >
                    “{body.pullQuote}”
                  </p>
                  <p
                    className="mt-6 font-inter text-[10px] uppercase font-semibold"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                    }}
                  >
                    From the paper
                  </p>
                </blockquote>

                <div>
                  <p
                    className="font-inter text-[10.5px] uppercase font-semibold pb-3 mb-5"
                    style={{
                      color: C.accentMetal,
                      letterSpacing: "0.28em",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    Download
                  </p>
                  <a
                    href="#"
                    data-testid="publication-download"
                    className="group inline-flex items-center gap-2 pb-1 font-inter text-[11px] uppercase font-semibold border-b transition-colors"
                    style={{
                      color: C.accentFunctional,
                      borderColor: C.accentFunctional,
                      letterSpacing: "0.24em",
                    }}
                  >
                    {pub.length} · PDF
                    <ArrowRight
                      size={12}
                      weight="bold"
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Related */}
      <section
        data-testid="publication-related"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="flex items-baseline justify-between mb-10">
            <div className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                Further reading
              </p>
            </div>
            <Link
              to="/concept-e/insights"
              data-testid="all-publications-link"
              className="group inline-flex items-center gap-2 pb-1 font-inter text-[10.5px] uppercase font-semibold border-b transition-colors"
              style={{
                color: C.accentMetal,
                borderColor: C.accentMetal,
                letterSpacing: "0.24em",
              }}
            >
              View all publications
              <ArrowRight
                size={11}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
          <ul
            className="grid md:grid-cols-2 gap-px"
            style={{ background: C.borderSoft }}
          >
            {related.map((r) => {
              const rIdx = publications.indexOf(r);
              const rBody = publicationBodies[rIdx];
              return (
                <li key={r.title} style={{ background: C.surfaceLight }}>
                  <Link
                    to={`/concept-e/insights/${rBody.slug}`}
                    className="group block p-8 h-full transition-colors"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = C.surfaceLight2)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = C.surfaceLight)
                    }
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-4">
                      <span
                        className="font-inter text-[10px] uppercase font-bold"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.26em",
                        }}
                      >
                        {r.tag} · {r.date}
                      </span>
                      <ArrowUpRight
                        size={18}
                        weight="thin"
                        style={{ color: C.accentMetal }}
                        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                      />
                    </div>
                    <h3
                      className="font-cormorant mb-3"
                      style={{
                        color: C.textOnLight,
                        fontSize: "1.3rem",
                        lineHeight: 1.18,
                      }}
                    >
                      {r.title}
                    </h3>
                    <p
                      className="font-inter text-[13px] leading-[1.6]"
                      style={{ color: C.inkOnLightSoft }}
                    >
                      {r.length}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <ConsultationBanner />
      <SiteFooter
        stamp="Concept 05"
        labelSuffix={pub.tag}
        markSubtitle="Leadership, People & Culture"
        descriptor="Faith 1st is a Leadership, People & Culture Consulting firm helping mission-driven and values-led organizations navigate high-stakes people moments through consulting, training, coaching, and facilitation."
      />
    </main>
  );
}
